import type { ExecutionResult } from "graphql";
import type { Sink, SubscribePayload } from "graphql-ws";
import type { PayloadData } from "relay-runtime";

type SubscriptionResult = ExecutionResult<PayloadData, Record<string, unknown>>;

type SubscriptionSink = Sink<SubscriptionResult>;

export type SubscriptionClient = {
  subscribe: (payload: SubscribePayload, sink: SubscriptionSink) => () => void;
};

type ActiveSubscription = {
  payload: SubscribePayload;
  sink: SubscriptionSink;
  onAttached?: (client: SubscriptionClient) => void;
  onDetached?: (client: SubscriptionClient) => void;
  active: boolean;
  connectionId: number;
  client: SubscriptionClient | null;
  unsubscribe: (() => void) | null;
};

/** Keeps active Relay subscriptions attached when the WebSocket client changes. */
export class SubscriptionManager {
  private client: SubscriptionClient | null = null;
  private nextConnectionId = 1;
  private readonly subscriptions = new Set<ActiveSubscription>();

  /** Replaces the transport and reattaches every active subscription to it. */
  setClient(client: SubscriptionClient | null) {
    if (this.client === client) return;

    const previousClient = this.client;
    this.client = client;

    this.subscriptions.forEach((subscription) => {
      this.detach(subscription, previousClient);

      if (client) {
        this.attach(subscription, client);
      }
    });
  }

  /** Adds a Relay subscription and returns its disposal function. */
  subscribe(
    payload: SubscribePayload,
    sink: SubscriptionSink,
    options: {
      onAttached?: (client: SubscriptionClient) => void;
      onDetached?: (client: SubscriptionClient) => void;
    } = {}
  ) {
    const subscription: ActiveSubscription = {
      payload,
      sink,
      onAttached: options.onAttached,
      onDetached: options.onDetached,
      active: true,
      connectionId: 0,
      client: null,
      unsubscribe: null,
    };

    this.subscriptions.add(subscription);

    if (this.client) {
      this.attach(subscription, this.client);
    }

    return () => {
      if (!subscription.active) return;

      subscription.active = false;
      this.subscriptions.delete(subscription);
      this.detach(subscription, subscription.client);
    };
  }

  private attach(subscription: ActiveSubscription, client: SubscriptionClient) {
    if (!subscription.active) return;

    const connectionId = this.nextConnectionId++;
    subscription.connectionId = connectionId;
    subscription.client = client;
    subscription.unsubscribe = client.subscribe(subscription.payload, {
      next: (value) => {
        if (subscription.active && subscription.connectionId === connectionId) {
          subscription.sink.next(value);
        }
      },
      error: (error) => {
        if (
          !subscription.active ||
          subscription.connectionId !== connectionId
        ) {
          return;
        }

        subscription.active = false;
        this.subscriptions.delete(subscription);
        subscription.sink.error(error);
        this.detach(subscription, client);
      },
      complete: () => {
        if (
          !subscription.active ||
          subscription.connectionId !== connectionId
        ) {
          return;
        }

        subscription.active = false;
        this.subscriptions.delete(subscription);
        subscription.sink.complete();
        this.detach(subscription, client);
      },
    });
    subscription.onAttached?.(client);
  }

  private detach(
    subscription: ActiveSubscription,
    client: SubscriptionClient | null
  ) {
    if (subscription.client !== client) return;

    subscription.connectionId = this.nextConnectionId++;
    subscription.client = null;
    const unsubscribe = subscription.unsubscribe;
    subscription.unsubscribe = null;
    unsubscribe?.();
    client && subscription.onDetached?.(client);
  }
}
